import { useState } from 'react'
import { evaluationAPI } from '../../services/api'

export default function BatchEvaluationPanel({ selectedJourneyIds }) {
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const runBatchEvaluation = async () => {
    if (selectedJourneyIds.length < 2) return
    setLoading(true)
    setError(null)
    try {
      const data = await evaluationAPI.evaluateBatch(selectedJourneyIds)
      setResults(data)
    } catch (err) {
      setError(err.response?.data?.detail || 'Batch evaluation failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <h3 className="card-title text-lg">Triage Evaluation</h3>
          <button
            className="btn btn-primary btn-sm"
            onClick={runBatchEvaluation}
            disabled={loading || selectedJourneyIds.length < 2}
          >
            {loading && <span className="loading loading-spinner loading-xs"></span>}
            Run Triage Evaluation ({selectedJourneyIds.length} selected)
          </button>
        </div>

        {selectedJourneyIds.length < 2 && !results && (
          <p className="text-sm text-base-content/60 mt-1">
            Select at least 2 completed journeys to run batch triage evaluation.
          </p>
        )}

        {error && (
          <div className="alert alert-error mt-2">
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 mt-3 text-sm text-base-content/60">
            <span className="loading loading-spinner loading-sm"></span>
            Extracting ISI scores and evaluating triage policy...
          </div>
        )}

        {results && !loading && (
          <div className="space-y-4 mt-3">
            {/* Summary stats */}
            <div className="stats stats-horizontal shadow w-full">
              <div className="stat">
                <div className="stat-title">Journeys</div>
                <div className="stat-value text-lg">{results.total_evaluated}</div>
              </div>
              <div className="stat">
                <div className="stat-title">Sensitivity</div>
                <div className="stat-value text-lg">{(results.sensitivity * 100).toFixed(0)}%</div>
              </div>
              <div className="stat">
                <div className="stat-title">Specificity</div>
                <div className="stat-value text-lg">{(results.specificity * 100).toFixed(0)}%</div>
              </div>
              <div className="stat">
                <div className="stat-title">Missed</div>
                <div className="stat-value text-lg text-error">{results.missed_escalations}</div>
              </div>
              <div className="stat">
                <div className="stat-title">Unnecessary</div>
                <div className="stat-value text-lg text-warning">{results.unnecessary_escalations}</div>
              </div>
            </div>

            {/* Per-journey table */}
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Journey</th>
                    <th>Final ISI</th>
                    <th>Triage Decision</th>
                    <th>Ground Truth</th>
                    <th>Correct</th>
                  </tr>
                </thead>
                <tbody>
                  {results.per_journey?.map(jr => (
                    <tr key={jr.journey_id} className={jr.error ? 'opacity-50' : ''}>
                      <td>
                        <div className="font-medium">{jr.persona_name || jr.journey_id?.slice(0, 8)}</div>
                        {jr.patient_name && (
                          <div className="text-xs text-base-content/50">{jr.patient_name}</div>
                        )}
                      </td>
                      <td>
                        {jr.error ? (
                          <span className="text-error text-xs">{jr.error}</span>
                        ) : (
                          <span className={`font-semibold ${jr.final_isi >= 22 ? 'text-error' : jr.final_isi >= 15 ? 'text-warning' : 'text-success'}`}>
                            {jr.final_isi}
                          </span>
                        )}
                      </td>
                      <td>
                        {!jr.error && (
                          <span className={`badge badge-sm ${
                            jr.triage_decision === 'escalate_to_specialist' ? 'badge-error' :
                            jr.triage_decision === 'escalate_to_clinician' ? 'badge-warning' :
                            'badge-success'
                          }`}>
                            {jr.triage_decision?.replace(/_/g, ' ')}
                          </span>
                        )}
                      </td>
                      <td>
                        {!jr.error && (
                          <span className={`badge badge-sm badge-outline ${
                            jr.ground_truth === 'should_escalate' ? 'badge-error' : 'badge-success'
                          }`}>
                            {jr.ground_truth?.replace(/_/g, ' ')}
                          </span>
                        )}
                      </td>
                      <td>
                        {!jr.error && (
                          <span className={`badge badge-sm ${jr.correct ? 'badge-success' : 'badge-error'}`}>
                            {jr.correct ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
